import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { Link } from "react-router-dom";

import { Domain } from "~/api/Api";
import { Authorizations } from "~/api/types";
import { domainKeys } from "~/components/siteSpeed/queryKeys";
import { MemberAuthorizations } from "~/components/siteSpeed/types";
import { canCollectionMember } from "~/helpers/authorizations";

import { apiClient } from "../../api/ApiClient";
import Button from "../mantine/button/Button";
import actionStyles from "../ui/dataTable/ActionColumn.module.scss";
import { ActionColumn } from "../ui/dataTable/DataTable";
import Spinner from "../ui/spinner/Spinner";
import type { ActionsSiteSpeedProps } from "./types";

type DomainsListResponse = {
  domains: Domain[];
  authorizations?: MemberAuthorizations;
};

const ActionsSiteSpeed: React.FC<ActionsSiteSpeedProps> = ({
  index,
  authorizations,
  domain,
  url,
}) => {
  const queryClient = useQueryClient();
  const domainId = domain.id;
  const domainQueryKey = domainKeys.detail(domainId);

  const auth = authorizations as unknown as Authorizations;
  const canShow = canCollectionMember(auth, index, "show");
  const canUpdate = canCollectionMember(auth, index, "update");
  const canRefresh = canCollectionMember(auth, index, "status");

  const { mutate: getStatus, isPending: isPendingStatus } = useMutation({
    mutationFn: async () => {
      const response = await apiClient.domains.statusDetail(domainId, {
        headers: {
          "Authorize-Member-Actions": "destroy,update,show,status",
        },
      });
      const domainUpdate = response.data?.domain;
      // @ts-expect-error BE types
      const domainAuthorizations = response.data?.authorizations;

      return { domainUpdate, domainAuthorizations };
    },
    onSuccess: ({ domainUpdate, domainAuthorizations }) => {
      // update domain cache
      queryClient.setQueryData(domainQueryKey, (prev: Domain | undefined) => ({
        ...(prev ?? {}),
        ...domainUpdate,
      }));

      // partial update domains list cache
      queryClient.setQueriesData<DomainsListResponse>(
        { queryKey: domainKeys.lists(), exact: false },
        (old) => {
          if (!old?.domains) return old;

          const domainIndex = old.domains.findIndex(
            (d: Domain) => d.id === domainUpdate.id,
          );
          if (domainIndex === -1) return old;

          const updatedDomains = old.domains.map((d: Domain) =>
            d.id === domainUpdate.id ? { ...d, ...domainUpdate } : d,
          );

          const auth = old.authorizations;
          const member = auth?.member;

          if (!auth || !member) {
            return {
              ...old,
              domains: updatedDomains,
            };
          }

          const updatedAuthorizations: MemberAuthorizations = {
            ...auth,
            member: member.map((row, i) =>
              i === domainIndex ? domainAuthorizations.member : row,
            ),
          };

          return {
            ...old,
            domains: updatedDomains,
            authorizations: updatedAuthorizations,
          };
        },
      );
    },
    onError: () => {
      enqueueSnackbar(
        `Unexpected error, please try again later or contact support.`,
        {
          variant: "error",
          persist: true,
        },
      );
    },
  });

  return (
    <ActionColumn>
      {(close) => (
        <>
          <Button
            disabled={!canShow || !canUpdate || isPendingStatus}
            variant="menuItem"
            className={actionStyles.popoverItem}
            onClick={() => getStatus()}
          >
            <span className={actionStyles.popoverItemTitle}>
              {isPendingStatus ? <Spinner size="1rem" /> : null}
              <span>Refresh Status</span>
            </span>
          </Button>

          {canRefresh ? (
            <Button
              variant="menuItem"
              component={Link}
              to={url}
              onClick={() => close()}
            >
              Details
            </Button>
          ) : (
            <Button variant="menuItem" disabled>
              Details
            </Button>
          )}
        </>
      )}
    </ActionColumn>
  );
};

export default ActionsSiteSpeed;
